import { AbstractLoyaltyService } from '@abstract-services/loyalty/1.0/loyalty.abstract-service';
import * as ngCommonHttp from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AddLoyaltyUserPoint$Params } from '@generated/tcea/fn/loyalty/add-loyalty-user-point';
import { AddLoyaltyUserReward$Params } from '@generated/tcea/fn/loyalty/add-loyalty-user-reward';
import { CreateLoyaltyProgram$Params } from '@generated/tcea/fn/loyalty/create-loyalty-program';
import { CreateLoyaltyProgramBonus$Params } from '@generated/tcea/fn/loyalty/create-loyalty-program-bonus';
import { CreateLoyaltyProgramReward$Params } from '@generated/tcea/fn/loyalty/create-loyalty-program-reward';
import { CreateLoyaltyProgramTier$Params } from '@generated/tcea/fn/loyalty/create-loyalty-program-tier';
import { GetLoyaltyProgram$Params } from '@generated/tcea/fn/loyalty/get-loyalty-program';
import { GetLoyaltyProgramBonus$Params } from '@generated/tcea/fn/loyalty/get-loyalty-program-bonus';
import { GetLoyaltyProgramReward$Params } from '@generated/tcea/fn/loyalty/get-loyalty-program-reward';
import { GetLoyaltyProgramTier$Params } from '@generated/tcea/fn/loyalty/get-loyalty-program-tier';
import { GetLoyaltyUser$Params } from '@generated/tcea/fn/loyalty/get-loyalty-user';
import { GetLoyaltyUserPoints$Params } from '@generated/tcea/fn/loyalty/get-loyalty-user-points';
import { GetLoyaltyUserRewards$Params } from '@generated/tcea/fn/loyalty/get-loyalty-user-rewards';
import { PostProgramOptin$Params } from '@generated/tcea/fn/loyalty/post-program-optin';
import { UpdateLoyaltyProgramBonus$Params } from '@generated/tcea/fn/loyalty/update-loyalty-program-bonus';
import { UpdateLoyaltyProgramReward$Params } from '@generated/tcea/fn/loyalty/update-loyalty-program-reward';
import { UpdateLoyaltyUserPoint$Params } from '@generated/tcea/fn/loyalty/update-loyalty-user-point';
import { UpdateLoyaltyUserReward$Params } from '@generated/tcea/fn/loyalty/update-loyalty-user-reward';
import {
  Bonus,
  LoyaltyUser,
  Program,
  ProgramOptin,
  Reward,
  Tier,
  UserOptin,
  UserPoint,
  UserReward,
} from '@generated/tcea/models';
import { LoyaltyService as generatedLoyaltyService } from '@generated/tcea/services/loyalty.service';
import { Observable, catchError, map, throwError } from 'rxjs';
import { filter } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class LoyaltyService extends AbstractLoyaltyService {
  constructor(private generatedLoyaltyService: generatedLoyaltyService) {
    super();
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @param params [object]
   * @returns observable of all programs
   */

  getAllPrograms(brandId): Observable<Program[]> {
    const params: GetLoyaltyProgram$Params = {
      'Active-Tenant-Identifier': brandId,
      programId: '',
    };
    return this.generatedLoyaltyService.getLoyaltyProgram(params).pipe(
      map((response: any) => {
        const { _embedded } = response;
        return _embedded?.programs || [];
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @param params [object]
   * @returns observable of all stores
   */

  getAllTiers(brandId, programId): Observable<Tier[]> {
    const params: GetLoyaltyProgramTier$Params = {
      'Active-Tenant-Identifier': brandId,
      programId,
      tierId: '',
    };
    return this.generatedLoyaltyService.getLoyaltyProgramTiers(params).pipe(
      map((response: any) => {
        const { _embedded } = response;
        return _embedded?.tiers || [];
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets program details by program id
   *
   * @param brandId [string]
   * @returns observable of all stores
   */
  getProgramById(brandId: string, programId: string): Observable<Program> {
    return this.generatedLoyaltyService
      .getLoyaltyProgram({ 'Active-Tenant-Identifier': brandId, programId })
      .pipe(
        map((response: any) => {
          const { links, ...program } = response;
          return program;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @param params [object]
   * @returns observable of all stores
   */

  getTierById(brandId, programId, tierId): Observable<Tier> {
    const params: GetLoyaltyProgramTier$Params = {
      'Active-Tenant-Identifier': brandId,
      programId,
      tierId,
    };
    return this.generatedLoyaltyService.getLoyaltyProgramTiers(params).pipe(
      map((response: any) => {
        const { _embedded } = response;
        const tier = _embedded?.tiers.find((tier) => tier.id === tierId);

        return tier || {};
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getAllRewards(brandId, programId): Observable<Reward[]> {
    const params: GetLoyaltyProgramReward$Params = {
      'Active-Tenant-Identifier': brandId,
      programId,
      rewardId: '',
    };
    return this.generatedLoyaltyService.getLoyaltyProgramRewards(params).pipe(
      map((response: any) => {
        const { _embedded } = response;
        return _embedded?.rewards || [];
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getRewardById(brandId, programId, rewardId): Observable<Reward> {
    const params: GetLoyaltyProgramReward$Params = {
      'Active-Tenant-Identifier': brandId,
      programId,
      rewardId,
    };
    return this.generatedLoyaltyService.getLoyaltyProgramRewards(params).pipe(
      map((response: any) => {
        const { _embedded } = response;
        const reward = _embedded?.rewards.find((reward) => reward.id === rewardId);
        return reward || {};
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getAllBonuses(brandId, programId): Observable<Bonus[]> {
    const params: GetLoyaltyProgramBonus$Params = {
      'Active-Tenant-Identifier': brandId,
      programId,
      bonusId: '',
    };
    return this.generatedLoyaltyService.getLoyaltyProgramBonuses(params).pipe(
      map((response: any) => {
        const { _embedded } = response;
        return _embedded?.bonuses || [];
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getLoyaltyUserByUserId(brandId, userId): Observable<LoyaltyUser> {
    const params: GetLoyaltyUser$Params = {
      'Active-Tenant-Identifier': brandId,
      userId,
    };
    return this.generatedLoyaltyService.getLoyaltyUser(params).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getPointsByUserId(brandId, userId): Observable<UserPoint[]> {
    const params: GetLoyaltyUserPoints$Params = {
      'Active-Tenant-Identifier': brandId,
      userId,
    };
    return this.generatedLoyaltyService.getLoyaltyUserPoints(params).pipe(
      map((response: any) => {
        const { _embedded } = response;
        return _embedded?.points || [];
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  addLoyaltyUserPoints(params: AddLoyaltyUserPoint$Params): Observable<UserPoint> {
    return this.generatedLoyaltyService.addLoyaltyUserPoint(params).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  updateLoyaltyUserPoints(params: UpdateLoyaltyUserPoint$Params): Observable<UserPoint> {
    return this.generatedLoyaltyService.updateLoyaltyUserPoint(params).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getRewardsByUserId(brandId, userId): Observable<UserReward[]> {
    const params: GetLoyaltyUserRewards$Params = {
      'Active-Tenant-Identifier': brandId,
      userId,
    };
    return this.generatedLoyaltyService.getLoyaltyUserRewards(params).pipe(
      map((response: any) => {
        const { _embedded } = response;
        return _embedded?.rewards || [];
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  addLoyaltyUserReward(params: AddLoyaltyUserReward$Params): Observable<Reward> {
    return this.generatedLoyaltyService.addLoyaltyUserReward(params).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  updateLoyaltyUserReward(params: UpdateLoyaltyUserReward$Params): Observable<UserPoint> {
    return this.generatedLoyaltyService.updateLoyaltyUserReward(params).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Create a loyalty program
   *
   * @param brandId [string]
   * @saveData : Program
   * @returns observable of a program
   */
  create(brandId: string, createData): Observable<Program> {
    let body: Program = createData;

    const params: CreateLoyaltyProgram$Params = {
      'Active-Tenant-Identifier': brandId,
      body,
    };

    return this.generatedLoyaltyService.createLoyaltyProgram(params).pipe(
      map((response: any) => {
        if (response) {
          return response;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Gets all coupons for current brand context
   *
   * @param brandId [string]
   * @returns observable of all stores
   */
  update(brandId: string, programId, updateData: Program): Observable<Program> {
    let body = updateData;
    return this.generatedLoyaltyService
      .updateLoyaltyProgram({ 'Active-Tenant-Identifier': brandId, body, programId })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Create a loyalty program
   * @param brandId [string]
   * @saveData : Program
   * @returns observable of a program
   */
  createTier(brandId: string, programId: string, createData): Observable<Tier> {
    let body: Tier = createData;

    const params: CreateLoyaltyProgramTier$Params = {
      'Active-Tenant-Identifier': brandId,
      programId,
      body,
    };

    return this.generatedLoyaltyService.createLoyaltyProgramTier(params).pipe(
      map((response: any) => {
        if (response) {
          return response;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * update a loyalty program tier
   *
   * @param brandId [string]
   * @param programId [string]
   * @param tierId [string]
   * @param updateData [Tier]
   * @returns observable of updated tier
   */
  updateTier(
    brandId: string,
    programId: string,
    tierId: string,
    updateData: Tier,
  ): Observable<Tier> {
    let body = updateData;
    return this.generatedLoyaltyService
      .updateLoyaltyProgramTier({ 'Active-Tenant-Identifier': brandId, programId, tierId, body })
      .pipe(
        map((response: any) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
      );
  }

  /**
   * Create a loyalty program
   *
   * @param brandId [string]
   * @saveData : Program
   * @returns observable of a reward
   */
  createReward(brandId: string, programId: string, createData): Observable<Reward> {
    let body: Reward = createData;

    const params: CreateLoyaltyProgramReward$Params = {
      'Active-Tenant-Identifier': brandId,
      programId,
      body,
    };

    return this.generatedLoyaltyService.createLoyaltyProgramReward(params).pipe(
      map((response: any) => {
        if (response) {
          return response;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * update a loyalty program reward
   *
   * @param brandId [string]
   * @param programId [string]
   * @param rewardId [string]
   * @param updateData [Tier]
   * @returns observable of updated reward
   */
  updateReward(
    brandId: string,
    programId: string,
    rewardId: string,
    updateData: Reward,
  ): Observable<Reward> {
    let body = updateData;

    const params: UpdateLoyaltyProgramReward$Params = {
      'Active-Tenant-Identifier': brandId,
      programId,
      rewardId,
      body,
    };

    return this.generatedLoyaltyService.updateLoyaltyProgramReward(params).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Create a loyalty program
   *
   * @param brandId [string]
   * @saveData : Program
   * @returns observable of a reward
   */
  createBonus(brandId: string, programId: string, createData): Observable<Bonus> {
    let body: Bonus = createData;

    const params: CreateLoyaltyProgramBonus$Params = {
      'Active-Tenant-Identifier': brandId,
      programId,
      body,
    };

    return this.generatedLoyaltyService.createLoyaltyProgramBonus(params).pipe(
      map((response: any) => {
        if (response) {
          return response;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * update a loyalty program reward
   *
   * @param brandId [string]
   * @param programId [string]
   * @param rewardId [string]
   * @param updateData [Tier]
   * @returns observable of updated reward
   */
  updateBonus(
    brandId: string,
    programId: string,
    bonusId: string,
    updateData: Bonus,
  ): Observable<Bonus> {
    let body = updateData;

    const params: UpdateLoyaltyProgramBonus$Params = {
      'Active-Tenant-Identifier': brandId,
      programId,
      bonusId,
      body,
    };

    return this.generatedLoyaltyService.updateLoyaltyProgramBonus(params).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Update user program opt-in status
   *
   * @param brandId [string]
   * @param programId [string]
   * @param body [ProgramOptin]
   * @returns observable of program opt-in status
   */
  updateUserProgramOptin(
    brandId: string,
    programId: string,
    body: UserOptin,
  ): Observable<ProgramOptin> {
    const params: PostProgramOptin$Params = {
      'Active-Tenant-Identifier': brandId,
      programId,
      body,
    };

    return this.generatedLoyaltyService.postProgramOptin(params).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }
}
